import type React from "react";
import type {
  GroupProps as AriaGroupProps,
  InputProps,
  LabelProps,
} from "react-aria-components";
import {
  Group,
  Input as RACInput,
  Label as RACLabel,
  composeRenderProps,
} from "react-aria-components";
import { tv } from "tailwind-variants";
import { cn } from "~/utils/cn";
import { focusRing } from "./utils";

export function Label(props: LabelProps) {
  return (
    <RACLabel
      {...props}
      className={cn(
        "body-sm-500 text-dark-600 mb-[0.375rem] inline-block w-fit cursor-default",
        props.className
      )}
    />
  );
}

interface FieldErrorProps {
  className?: string;
  children: React.ReactNode;
}

export function FieldError(props: FieldErrorProps) {
  if (!props.children) {
    return null;
  }
  return (
    <div
      {...props}
      className={cn(
        props.className,
        "body-sm-500 text-system-red mt-[0.375rem]"
      )}
    />
  );
}

export const fieldStyles = tv({
  variants: {
    isError: {
      true: "border-system-red",
    },
    isDisabled: {
      true: "border-gray-200 text-dark-300 cursor-not-allowed",
    },
    variant: {
      filter:
        "border-outline-100 h-[1.75rem] flex items-center overflow-clip rounded-[0.5rem] border",
    },
  },
});

export const fieldGroupStyles = tv({
  extend: focusRing,
  base: "group flex items-center h-9 bg-white border-2 rounded-lg overflow-hidden",
  variants: fieldStyles.variants,
});

interface GroupProps extends AriaGroupProps {
  variant?: "filter";
}

export function FieldGroup(props: GroupProps) {
  return (
    <Group
      {...props}
      className={composeRenderProps(props.className, (className, renderProps) =>
        fieldGroupStyles({ ...renderProps, variant: props.variant, className })
      )}
    />
  );
}

export function Input(props: InputProps) {
  return <RACInput {...props} />;
}

export function Description(props: { children: React.ReactNode }) {
  // TODO: add styling from design system
  return <p className="text-sm text-gray-500">{props.children}</p>;
}

export function Field({
  label,
  children,
  className,
}: {
  label?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("w-full", className)}>
      {label && <Label>{label}</Label>}
      {children}
    </div>
  );
}
